import { Box, useTheme } from "@mui/material";
import { EmblemMark } from "./EmblemMark";

/**
 * The landing page's orbit scene, ported for the onboarding hook screen: the
 * Insight emblem on a white tile with two counter-rotating shells of real
 * company-logo chips (chips counter-spin so logos stay upright). Every
 * company the engine covers, in orbit around Insight.
 *
 * Chip placements are precomputed literals (verbatim from the landing's
 * SignalFlow) and logos are local assets in public/stock-logos — the hook
 * screen makes zero network calls.
 */

// Orbiting chips. Inner shell: 4 chips at 90° steps; outer shell: 5 chips at
// 72° steps. MA and NFLX start at each ring's 6 o'clock point; the chips'
// counter-spin keeps every logo perfectly upright as it travels.
const ORBIT_SHELLS: {
  spin: "orbit-cw" | "orbit-ccw";
  size: number;
  chips: { symbol: string; x: number; y: number }[];
}[] = [
  {
    spin: "orbit-cw",
    size: 132,
    chips: [
      { symbol: "NVDA", x: 50, y: 0 },
      { symbol: "GOOGL", x: 100, y: 50 },
      { symbol: "MA", x: 50, y: 100 },
      { symbol: "MSFT", x: 0, y: 50 },
    ],
  },
  {
    spin: "orbit-ccw",
    size: 248,
    chips: [
      { symbol: "RKLB", x: 20.61, y: 9.55 },
      { symbol: "NBIS", x: 79.39, y: 9.55 },
      { symbol: "TSLA", x: 97.55, y: 65.45 },
      { symbol: "NFLX", x: 50, y: 100 },
      { symbol: "AUR", x: 2.45, y: 65.45 },
    ],
  },
];


export const OrbitCloud = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const ringBorder = isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.12)";
  const chipBorder = isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.15)";

  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        width: 288,
        height: 288,
        mx: "auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "@keyframes orbitSpin": { to: { transform: "rotate(360deg)" } },
        "@keyframes orbitBreathe": {
          from: { opacity: 0.65, transform: "scale(0.94)" },
          to: { opacity: 1, transform: "scale(1.06)" },
        },
        "& .orbit-cw": { animation: "orbitSpin 26s linear infinite" },
        "& .orbit-cw .orbit-chip": { animation: "orbitSpin 26s linear infinite reverse" },
        "& .orbit-ccw": { animation: "orbitSpin 42s linear infinite reverse" },
        "& .orbit-ccw .orbit-chip": { animation: "orbitSpin 42s linear infinite" },
        "& .orbit-ambient": {
          animation: "orbitBreathe 4.5s ease-in-out infinite alternate",
        },
        "@media (prefers-reduced-motion: reduce)": {
          "& .orbit-cw, & .orbit-ccw, & .orbit-chip, & .orbit-ambient": {
            animation: "none",
          },
        },
      }}
    >
      {/* Soft breathing ambient behind the core. */}
      <Box
        className="orbit-ambient"
        sx={{
          position: "absolute",
          inset: "22%",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(34,197,94,0.25) 0%, rgba(34,197,94,0.08) 45%, transparent 70%)",
        }}
      />

      {ORBIT_SHELLS.map(({ spin, size, chips }) => (
        <Box
          key={spin}
          className={spin}
          sx={{
            position: "absolute",
            width: size,
            height: size,
            borderRadius: "50%",
            border: `2px dotted ${ringBorder}`,
          }}
        >
          {chips.map(({ symbol, x, y }) => (
            <Box
              key={symbol}
              sx={{
                position: "absolute",
                left: `${x}%`,
                top: `${y}%`,
                transform: "translate(-50%, -50%)",
              }}
            >
              <Box
                className="orbit-chip"
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: `1px solid ${chipBorder}`,
                  backgroundColor: "#fafafa",
                }}
              >
                <Box
                  component="img"
                  src={`/stock-logos/${symbol}.png`}
                  alt=""
                  sx={{ width: 24, height: 24, borderRadius: "50%", objectFit: "contain" }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      ))}

      {/* Centre tile: the emblem, matching the landing's core. */}
      <Box
        sx={{
          position: "relative",
          width: 56,
          height: 56,
          borderRadius: 3.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: `1px solid ${chipBorder}`,
          backgroundColor: "#fafafa",
        }}
      >
        <EmblemMark height={28} />
      </Box>
    </Box>
  );
};
